import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  changePassword as changePasswordApi,
  login as loginApi,
  logout as logoutApi,
  profile as profileApi,
  updateProfile as updateProfileApi,
  uploadAvatar as uploadAvatarApi,
  removeAvatar as removeAvatarApi,
  refresh as refreshApi,
  register as registerApi,
} from '../services/auth';
import {
  clearAuth,
  getAccessToken,
  getAuthState,
  getRefreshToken,
  getUser,
  saveAuth,
} from '../utils/auth';

const AuthContext = createContext(null);

function mapProfileToUser(profile, fallback = {}) {
  if (!profile) return fallback;
  return {
    userUuid: profile.uuid || profile.userUuid || fallback.userUuid,
    userName: profile.name || profile.displayName || fallback.userName,
    email: profile.email ?? fallback.email ?? null,
    emailVerified: Boolean(profile.emailVerified ?? fallback.emailVerified),
    avatarUrl: profile.avatarUrl ?? fallback.avatarUrl ?? null,
  };
}

function mergeUserUpdate(prev, patch) {
  return { ...(prev || {}), ...patch };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getUser());
  const [loading, setLoading] = useState(false);

  const syncUserFromProfile = useCallback((profile, fallback) => {
    const nextUser = mapProfileToUser(profile, fallback ?? getUser() ?? {});
    setUser(nextUser);
    const auth = getAuthState();
    if (auth) {
      saveAuth({ ...auth, user: nextUser });
    }
    return nextUser;
  }, []);

  useEffect(() => {
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    if (!accessToken && !refreshToken) {
      return;
    }

    const recover = async () => {
      try {
        const profile = await profileApi();
        syncUserFromProfile(profile, getUser());
      } catch (_) {
        if (!getRefreshToken()) {
          clearAuth();
          setUser(null);
          return;
        }
        try {
          const auth = await refreshApi({ refreshToken: getRefreshToken() });
          saveAuth(auth);
          const profile = await profileApi();
          syncUserFromProfile(profile, buildLegacyUserFromAuth(auth));
        } catch (_) {
          clearAuth();
          setUser(null);
        }
      }
    };

    recover();
  }, [syncUserFromProfile]);

  const value = useMemo(
    () => ({
      user,
      loading,
      async register(payload) {
        setLoading(true);
        try {
          const auth = await registerApi(payload);
          const nextUser = mergeUserUpdate(buildLegacyUserFromAuth(auth), {
            email: payload.email || null,
            emailVerified: false,
          });
          saveAuth({ ...auth, user: nextUser });
          setUser(nextUser);
          return { ...auth, ...nextUser };
        } finally {
          setLoading(false);
        }
      },
      async login(payload) {
        setLoading(true);
        try {
          const auth = await loginApi(payload);
          saveAuth(auth);
          let nextUser = buildLegacyUserFromAuth(auth);
          try {
            const profile = await profileApi();
            nextUser = mapProfileToUser(profile, nextUser);
          } catch (_) {
            // profile fetch is best-effort after login
          }
          saveAuth({ ...getAuthState(), ...auth, user: nextUser });
          setUser(nextUser);
          return { ...auth, ...nextUser };
        } finally {
          setLoading(false);
        }
      },
      async logout() {
        setLoading(true);
        try {
          await logoutApi();
        } finally {
          clearAuth();
          setUser(null);
          setLoading(false);
        }
      },
      async changePassword(payload) {
        setLoading(true);
        try {
          await changePasswordApi(payload);
          clearAuth();
          setUser(null);
        } finally {
          setLoading(false);
        }
      },
      async updateProfile(payload) {
        setLoading(true);
        try {
          const profile = await updateProfileApi(payload);
          return syncUserFromProfile(profile);
        } finally {
          setLoading(false);
        }
      },
      async refreshProfile() {
        const profile = await profileApi();
        return syncUserFromProfile(profile);
      },
      async uploadAvatar(file) {
        setLoading(true);
        try {
          const result = await uploadAvatarApi(file);
          const profile = result?.profile || result;
          syncUserFromProfile(profile);
          return result;
        } finally {
          setLoading(false);
        }
      },
      async removeAvatar() {
        setLoading(true);
        try {
          const profile = await removeAvatarApi();
          return syncUserFromProfile(profile);
        } finally {
          setLoading(false);
        }
      },
      markEmailVerified(email) {
        setUser((prev) => {
          const nextUser = mergeUserUpdate(prev, {
            email: email || prev?.email,
            emailVerified: true,
          });
          const auth = getAuthState();
          if (auth) {
            saveAuth({ ...auth, user: nextUser });
          }
          return nextUser;
        });
      },
    }),
    [user, loading, syncUserFromProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function buildLegacyUserFromAuth(auth = {}) {
  return {
    userUuid: auth.userUuid || auth.user?.userUuid,
    userName: auth.userName || auth.user?.userName,
    email: auth.email || auth.user?.email || null,
    emailVerified: Boolean(auth.emailVerified ?? auth.user?.emailVerified),
    avatarUrl: auth.avatarUrl || auth.user?.avatarUrl || null,
  };
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
